import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bank, Copy, CheckCircle, ArrowRight, Truck, MapPin } from "@phosphor-icons/react";
import { carrierTrackingUrl } from "@/lib/tracking";

export default function ManualPayment() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!orderId) { setError(true); return; }
    Promise.all([
      api.get(`/orders/summary/${orderId}`),
      api.get("/payments/config"),
    ]).then(([o, c]) => { setOrder(o.data); setCfg(c.data); })
      .catch(() => setError(true));
  }, [orderId]);

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  if (error) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6 py-16 text-center">
        <div>
          <h1 className="font-head text-3xl mb-3">Order not found</h1>
          <Link to="/shop" className="inline-block bg-primary text-black label-mono py-4 px-8 mt-4">Back to Shop</Link>
        </div>
      </div>
    );
  }

  if (!order || !cfg) {
    return <div className="min-h-[70vh] grid place-items-center text-muted-foreground font-mono text-sm">Loading…</div>;
  }

  const labels = { zelle: "Zelle", venmo: "Venmo", cashapp: "Cash App", gpay: "Google Pay", applepay: "Apple Pay" };
  const method = labels[order.payment_method] ? order.payment_method : "zelle";
  const label = labels[method];
  const recipient = cfg[`${method}_recipient`];
  const recipientName = cfg[`${method}_name`];

  const appHint = {
    zelle: "bank",
    venmo: "Venmo",
    cashapp: "Cash App",
    gpay: "Google Pay",
    applepay: "Messages",
  }[method];

  return (
    <div className="max-w-xl mx-auto px-6 py-16">
      <div className="text-center mb-10 fade-up">
        <div className="w-16 h-16 rounded-full border border-primary/30 bg-primary/10 grid place-items-center mx-auto mb-6">
          <Bank size={32} className="text-primary" />
        </div>
        <h1 className="font-head text-3xl mb-3">Order Received — Send Payment</h1>
        <p className="text-muted-foreground">Your order is reserved. Complete your <span className="text-foreground">{label}</span> transfer to confirm it, then we'll ship it out.</p>
      </div>

      <div data-testid="manual-instructions" className="border border-black/10 bg-surface p-8 space-y-6">
        <Row label="Send exactly" value={`$${Number(order.amount).toFixed(2)}`} testid="manual-amount" onCopy={() => copy(Number(order.amount).toFixed(2), "Amount")} big />
        <Row label={`${label} to (${recipientName || "Recipient"})`} value={recipient} testid="manual-recipient" onCopy={() => copy(recipient, `${label} recipient`)} />
        <Row label="Payment reference (add to note/memo)" value={order.reference} testid="manual-reference" onCopy={() => copy(order.reference, "Reference")} />

        <div className="border-t border-black/10 pt-6 text-sm text-muted-foreground leading-relaxed">
          <span className="label-mono text-primary block mb-2">How to pay</span>
          <ol className="list-decimal list-inside space-y-1">
            <li>Open your {appHint} app and choose <span className="text-foreground">{label}</span>.</li>
            <li>Send <span className="text-foreground">${Number(order.amount).toFixed(2)}</span> to <span className="text-foreground">{recipient}</span>.</li>
            <li>Put reference <span className="text-foreground">{order.reference}</span> in the note/memo.</li>
            <li>We'll confirm your payment and ship your order.</li>
          </ol>
        </div>

        <div className="flex items-center gap-2 text-[0.7rem] text-accent">
          <CheckCircle size={16} weight="duotone" />
          A confirmation has been sent to {order.customer_email}
        </div>
      </div>

      {order.shipping_address?.name && (
        <div data-testid="confirm-shipping-address" className="border border-black/10 bg-surface p-6 mt-6">
          <span className="label-mono text-primary text-[0.6rem] flex items-center gap-1.5 mb-2"><MapPin size={13} weight="fill" /> Shipping to</span>
          <p className="text-sm text-foreground leading-relaxed">
            {order.shipping_address.name}<br />
            {order.shipping_address.line1}{order.shipping_address.line2 ? <>, {order.shipping_address.line2}</> : null}<br />
            {[order.shipping_address.city, order.shipping_address.state, order.shipping_address.postal_code].filter(Boolean).join(", ")}
            {order.shipping_address.phone ? <><br />{order.shipping_address.phone}</> : null}
          </p>
          <p className="label-mono text-muted-foreground text-[0.6rem] mt-3">Please make sure this address is correct. Need a change? Reply to your confirmation email.</p>
        </div>
      )}

      {order.tracking_number && (
        <div data-testid="confirm-tracking" className="border border-accent/30 bg-accent/5 p-6 mt-6">
          <span className="label-mono text-accent text-[0.6rem] flex items-center gap-1.5 mb-2"><Truck size={13} weight="fill" /> Shipped</span>
          <p className="text-sm text-foreground">
            {order.carrier ? `${order.carrier} · ` : ""}
            {carrierTrackingUrl(order.carrier, order.tracking_number) ? (
              <a data-testid="confirm-tracking-link" href={carrierTrackingUrl(order.carrier, order.tracking_number)} target="_blank" rel="noopener noreferrer" className="font-mono text-primary underline underline-offset-2 hover:text-[#C9A227] transition-colors break-all">{order.tracking_number}</a>
            ) : (
              <span className="font-mono break-all">{order.tracking_number}</span>
            )}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-8">
        <Link to="/account" data-testid="manual-view-orders" className="flex items-center justify-center gap-2 bg-primary text-black label-mono py-4">View My Orders <ArrowRight size={16} weight="bold" /></Link>
        <Link to="/shop" className="border border-black/15 label-mono py-4 text-center hover:border-primary hover:text-primary transition-colors">Continue Shopping</Link>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy, testid, big }) {
  return (
    <div>
      <span className="label-mono text-muted-foreground text-[0.6rem] block mb-1">{label}</span>
      <div className="flex items-center justify-between gap-3 border border-black/15 bg-white px-4 py-3">
        <span data-testid={testid} className={`font-mono ${big ? "text-2xl text-primary" : "text-base"} break-all`}>{value}</span>
        <button onClick={onCopy} className="text-muted-foreground hover:text-primary transition-colors shrink-0" aria-label={`Copy ${label}`}>
          <Copy size={18} />
        </button>
      </div>
    </div>
  );
}
